import React from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'

/**
 * Renders a single menu item in the application layout sidebar.
 * The menu item includes an icon and a title, and is highlighted when active.
 *
 * @module appLayout/AppLayoutMenuItems
 * @description Renders a single menu item in the application layout sidebar.
 * The menu item includes an icon and a title, and is highlighted when active.
 * @example
 * const menuItem = {
 *   routePath: 'dashboard',
 *   pageTitle: 'Dashboard',
 *   apiEndPointSchema: { icon: 'dashboard-2' }
 * };
 * const setToggleMenu = () => {};
 * const urlLink = 'dashboard';
 *
 * return (
 *   <AppLayoutMenuItems
 *     menuItem={menuItem}
 *     setToggleMenu={setToggleMenu}
 *     urlLink={urlLink}
 *   />
 * );
 *
 * @param {Object} menuItem - The menu item data containing route and display information.
 * @param {string} menuItem.routePath - The path for the menu item's route.
 * @param {string} menuItem.pageTitle - The title of the menu item to display.
 * @param {Object} menuItem.apiEndPointSchema - Schema containing endpoint details.
 * @param {string} [menuItem.apiEndPointSchema.icon='dashboard-2'] - Icon name for the menu item (defaults to 'dashboard-2').
 * @param {function} setToggleMenu - Function to toggle the sidebar menu visibility.
 * @param {string} urlLink - The current URL path to compare with the menu item's route.
 * @returns {React.Element} The rendered menu item component.
 */
export const AppLayoutMenuItems = ({
  menuItem,
  setToggleMenu,
  urlLink,
  isUndefined,
  deActivateMenuGroup,
  linkPrefix,
}) => {
  return (
    <li className="trtui-nav-item" style={{ paddingLeft: isUndefined ? '0px' : '0.5rem' }}>
      {menuItem ? (
        <Link
          className={`trtui-nav-link trtui-menu-link ${urlLink === menuItem.routePath ? 'trtui-active' : ''}`}
          to={`/${linkPrefix ? linkPrefix : 'console'}/${menuItem.routePath}`}
          onClick={() => {
            if (isUndefined) {
              deActivateMenuGroup()
            }
            setToggleMenu(false)
          }}
        >
          <i
            data-testid="menuItemIcon"
            className={`${menuItem.apiEndPointSchema.icon || 'ri-apps-line'}`}
          />
          <span data-key={menuItem.pageTitle}>{menuItem.pageTitle}</span>
        </Link>
      ) : (
        <></>
      )}
    </li>
  )
}

AppLayoutMenuItems.propTypes = {
  menuItem: PropTypes.object,
  setToggleMenu: PropTypes.func,
  urlLink: PropTypes.string,
  isUndefined: PropTypes.bool,
  deActivateMenuGroup: PropTypes.func,
  linkPrefix: PropTypes.string,
}
